/**
 * RangeCalendar — a two-click date-range picker built on the shadcn Calendar.
 *
 * Why this exists: react-day-picker's built-in `mode="range"` won't let you
 * re-anchor the start once a full range is selected. This drives selection
 * manually off single-day clicks instead (the approach proven on the Dashboard
 * revenue calendar): a click while a complete range is showing starts fresh;
 * otherwise it sets the end (swapping if the end is earlier). Optional span cap
 * and min/max bounds.
 *
 * Controlled: the parent holds `value` ({ from, to? }) and reacts to `onChange`
 * (fired on every click with the new { from, to? }). The parent decides what a
 * completed range means — commit immediately, or stage behind an Apply button.
 */
import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { differenceInCalendarDays, addDays } from 'date-fns';

export default function RangeCalendar({
  value,
  onChange,
  numberOfMonths = 1,
  disabledBefore,
  disabledAfter,
  maxSpanDays,
  defaultMonth,
}) {
  const handlePick = (clicked) => {
    if (!clicked) return;
    // A genuine multi-day range is "complete"; a same-day pick is still in
    // progress (the next click extends it) — matches the dashboard's feel.
    const complete = value?.from && value?.to && value.from.getTime() !== value.to.getTime();
    if (!value?.from || complete) {
      onChange({ from: clicked, to: undefined }); // (re)start the range here
      return;
    }
    let from = value.from;
    let to = clicked;
    if (to < from) { [from, to] = [to, from]; }
    if (maxSpanDays && differenceInCalendarDays(to, from) > maxSpanDays - 1) {
      to = addDays(from, maxSpanDays - 1); // cap the span
    }
    onChange({ from, to });
  };

  const disabled = [];
  if (disabledAfter) disabled.push({ after: disabledAfter });
  if (disabledBefore) disabled.push({ before: disabledBefore });

  return (
    <Calendar
      mode="single"
      selected={undefined}
      onSelect={handlePick}
      disabled={disabled.length ? disabled : undefined}
      numberOfMonths={numberOfMonths}
      defaultMonth={defaultMonth}
      modifiers={{
        selected: value?.from ? [value.from, value.to].filter(Boolean) : [],
        inRange: value?.from && value?.to ? { from: value.from, to: value.to } : undefined,
      }}
      modifiersClassNames={{
        selected: 'bg-emerald-600 text-white font-semibold',
        inRange: 'bg-emerald-100 text-emerald-900',
        today: 'bg-amber-100 text-amber-900 font-semibold ring-1 ring-amber-300',
      }}
      initialFocus
    />
  );
}
